import React, { createContext, useContext, useState } from 'react'

import type { buttonVariants } from '@/components/ui/button'
import type { VariantProps } from 'class-variance-authority'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface AlertDialogOptions {
  title: string
  description?: string
  content?: React.ReactNode
  confirmText?: string
  cancelText?: string
  confirmVariant?: VariantProps<typeof buttonVariants>['variant']
  onConfirm?: () => void | Promise<void>
  onCancel?: () => void
  dangerouslyUseHTML?: boolean
}

interface AlertDialogContextType {
  showAlert: (options: AlertDialogOptions) => Promise<boolean>
}

const AlertDialogContext = createContext<AlertDialogContextType | undefined>(
  undefined,
)

interface AlertDialogState {
  isOpen: boolean
  options: AlertDialogOptions & {
    resolve: (value: boolean) => void
  }
}

export function AlertDialogProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [state, setState] = useState<AlertDialogState | null>(null)

  const showAlert = (options: AlertDialogOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setState({
        isOpen: true,
        options: {
          ...options,
          resolve,
        },
      })
    })
  }

  const handleClose = (confirmed: boolean) => {
    if (state) {
      if (confirmed && state.options.onConfirm) {
        state.options.onConfirm()
      } else if (!confirmed && state.options.onCancel) {
        state.options.onCancel()
      }
      state.options.resolve(confirmed)

      // Start closing animation
      setState({
        ...state,
        isOpen: false,
      })

      // Remove from DOM after animation completes
      setTimeout(() => {
        setState(null)
      }, 200) // Match the animation duration from dialog component
    }
  }

  return (
    <AlertDialogContext.Provider value={{ showAlert }}>
      {children}
      {state && (
        <Dialog
          open={state.isOpen}
          onOpenChange={(open) => {
            if (!open) handleClose(false)
          }}
        >
          <DialogContent showCloseButton={false}>
            <DialogHeader>
              <DialogTitle>{state.options.title}</DialogTitle>
              {state.options.description && (
                <DialogDescription>
                  {state.options.dangerouslyUseHTML ? (
                    <span
                      dangerouslySetInnerHTML={{
                        __html: state.options.description,
                      }}
                    />
                  ) : (
                    state.options.description
                  )}
                </DialogDescription>
              )}
              {state.options.content && <div>{state.options.content}</div>}
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => handleClose(false)}>
                {state.options.cancelText || 'Cancel'}
              </Button>
              <Button
                variant={state.options.confirmVariant || 'default'}
                onClick={() => handleClose(true)}
              >
                {state.options.confirmText || 'Confirm'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </AlertDialogContext.Provider>
  )
}

export function useAlertDialog(): AlertDialogContextType {
  const context = useContext(AlertDialogContext)
  if (!context) {
    throw new Error('useAlertDialog must be used within AlertDialogProvider')
  }
  return context
}

interface DeleteConfirmationOptions {
  itemName: string
  itemDetails?: string
  onConfirm?: () => void | Promise<void>
}

// Export a simplified delete confirmation hook
export function useDeleteConfirmation() {
  const { showAlert } = useAlertDialog()

  const showDeleteConfirmation = async (
    options: DeleteConfirmationOptions,
  ): Promise<boolean> => {
    const { itemName, itemDetails, onConfirm } = options
    return showAlert({
      title: `Delete ${itemName}`,
      description: itemDetails
        ? `Are you sure you want to delete <strong>${itemDetails}</strong>? This action cannot be undone.`
        : `Are you sure you want to delete this ${itemName.toLowerCase()}? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmVariant: 'destructive',
      dangerouslyUseHTML: true,
      onConfirm,
    })
  }

  return { showDeleteConfirmation }
}
